import React, { useState, useEffect, useRef } from 'react';
import { ProgressTracker, ProgressTrackerItem } from '@emorg-prd/standard-react';
import { useNavigate, useLocation } from 'react-router-dom';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import SubscriptionRequest from '../../models/entities/SubscriptionRequest';
import { ResourceGroupEntry } from './components/AddResourceGroupModal';
import { ExemptionDetails } from './ExemptionDetailsForm';
import PolicyExemptionRequestSelectSubscriptions from './PolicyExemptionRequestSelectSubscriptions';
import PolicyExemptionRequestSelectRG from './PolicyExemptionRequestSelectRG';
import PolicyExemptionRequestSelectAssignment from './PolicyExemptionRequestSelectAssignment';
import PolicyExemptionRequestSelectPolicy from './PolicyExemptionRequestSelectPolicy';
import PolicyExemptionSubmit from './PolicyExemptionSubmit';
import SubscriptionRequestsService from '../../services/SubscriptionRequestsService';
import { AzureResourceModel } from '../../models/viewModels/Subscriptions';
import { PolicyAssignmentResponse } from '../../models/viewModels/PolicyExemptionRequests/PolicyAssignmentResponse';
import { PolicyDefinitionResponse } from '../../models/viewModels/PolicyExemptionRequests/PolicyDefinitionResponse';

const PolicyExemptionHome = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const preselectedSubscription: SubscriptionRequest | undefined = (location.state as any)?.preselectedSubscription;

	const [currentStep, setCurrentStep] = useState(1);
	const [cachedSubscriptions, setCachedSubscriptions] = useState<SubscriptionRequest[]>([]);
	const [selectedSubscriptions, setSelectedSubscriptions] = useState<SubscriptionRequest[]>(
		preselectedSubscription ? [preselectedSubscription] : []
	);
	const [entriesBySubscription, setEntriesBySubscription] = useState<Record<number, ResourceGroupEntry[]>>({});
	const [bulkExpirationBySubscription, setBulkExpirationBySubscription] = useState<Record<number, string>>({});
	const [rgsBySubscription, setRGsBySubscription] = useState<Record<number, AzureResourceModel[]>>({});
	const [resourcesByRG, setResourcesByRG] = useState<Record<string, AzureResourceModel[]>>({});
	const [selectedAssignmentBySubscription, setSelectedAssignmentBySubscription] = useState<
		Record<number, PolicyAssignmentResponse>
	>({});
	const [selectedPoliciesBySubscription, setSelectedPoliciesBySubscription] = useState<
		Record<number, PolicyDefinitionResponse[]>
	>({});
	const [exemptionDetails, setExemptionDetails] = useState<ExemptionDetails>({
		exceptionDescription: '',
		category: '2',
		link: '',
		justification: '',
	});
	const [assignmentsBySubscription, setAssignmentsBySubscription] = useState<
		Record<number, PolicyAssignmentResponse[]>
	>({});
	const [loadingAssignments, setLoadingAssignments] = useState(false);
	const subscriptionRequestsService = useRef(new SubscriptionRequestsService());

	// Prefetch assignments as soon as subscriptions are known (step >= 2).
	// Skips subscriptions already cached to avoid redundant API calls.
	useEffect(() => {
		if (currentStep < 2 || selectedSubscriptions.length === 0) return;
		const missing = selectedSubscriptions.filter((sub) => !(sub.id in assignmentsBySubscription));
		if (missing.length === 0) return;

		setLoadingAssignments(true);
		const fetchAll = async () => {
			try {
				const results: Record<number, PolicyAssignmentResponse[]> = {};
				await Promise.all(
					missing.map(async (sub) => {
						//TODO: Check if policies definitions is by tenant or subscription. If by tenant, we can fetch all at once instead of per subscription
						results[sub.id] =
							await subscriptionRequestsService.current.getSubscriptionPolicyAssignmentsById(sub.id);
					})
				);
				setAssignmentsBySubscription((prev) => ({ ...prev, ...results }));
			} catch (error) {
				console.error('Error prefetching policy assignments:', error);
			} finally {
				setLoadingAssignments(false);
			}
		};
		fetchAll();
	}, [currentStep, selectedSubscriptions]);

	const renderStep = () => {
		switch (currentStep) {
			case 1:
				return (
					<PolicyExemptionRequestSelectSubscriptions
						cachedSubscriptions={cachedSubscriptions.length > 0 ? cachedSubscriptions : undefined}
						preselectedSubscription={preselectedSubscription}
						initialSelectedSubscriptions={
							selectedSubscriptions.length > 0 ? selectedSubscriptions : undefined
						}
						onNext={(selected, cached) => {
							setSelectedSubscriptions(selected);
							setCachedSubscriptions(cached);
							setCurrentStep(2);
						}}
						onCancel={() => navigate('/subscriptions')}
					/>
				);
			case 2:
				return (
					<PolicyExemptionRequestSelectRG
						selectedSubscriptions={selectedSubscriptions}
						initialEntriesBySubscription={entriesBySubscription}
						initialBulkExpirationBySubscription={bulkExpirationBySubscription}
						initialRGsBySubscription={rgsBySubscription}
						initialResourcesByRG={resourcesByRG}
						onNext={(entries, bulkExpiration, rgs, resources) => {
							setEntriesBySubscription(entries);
							setBulkExpirationBySubscription(bulkExpiration);
							setRGsBySubscription(rgs);
							setResourcesByRG(resources);
							setCurrentStep(3);
						}}
						onBack={() => setCurrentStep(1)}
					/>
				);
			case 3:
				return (
					<PolicyExemptionRequestSelectAssignment
						selectedSubscriptions={selectedSubscriptions}
						entriesBySubscription={entriesBySubscription}
						initialSelectedAssignmentBySubscription={selectedAssignmentBySubscription}
						cachedAssignmentsBySubscription={assignmentsBySubscription}
						loadingAssignments={loadingAssignments}
						onNext={(assignments) => {
							setSelectedAssignmentBySubscription(assignments);
							setCurrentStep(4);
						}}
						onBack={() => setCurrentStep(2)}
					/>
				);
			case 4:
				return (
					<PolicyExemptionRequestSelectPolicy
						selectedSubscriptions={selectedSubscriptions}
						entriesBySubscription={entriesBySubscription}
						selectedAssignmentBySubscription={selectedAssignmentBySubscription}
						initialSelectedPoliciesBySubscription={selectedPoliciesBySubscription}
						initialExemptionDetails={exemptionDetails}
						onNext={(policies, details) => {
							setSelectedPoliciesBySubscription(policies);
							setExemptionDetails(details);
							setCurrentStep(5);
						}}
						onBack={() => setCurrentStep(3)}
					/>
				);
			case 5:
				return (
					<PolicyExemptionSubmit
						selectedSubscriptions={selectedSubscriptions}
						entriesBySubscription={entriesBySubscription}
						bulkExpirationBySubscription={bulkExpirationBySubscription}
						selectedAssignmentBySubscription={selectedAssignmentBySubscription}
						selectedPoliciesBySubscription={selectedPoliciesBySubscription}
						exemptionDetails={exemptionDetails}
						onBack={() => setCurrentStep(4)}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<SectionHomeWrapper title="New Policy Exemption Request">
			<ProgressTracker
				labelPosition="bottom"
				onclick={(step) => console.log('step selected:', step) /* setCurrentStep(step) */}
			>
				<ProgressTrackerItem completed={currentStep > 1} current={currentStep === 1}>
					{' '}
					Select Subscriptions{' '}
				</ProgressTrackerItem>
				<ProgressTrackerItem completed={currentStep > 2} current={currentStep === 2}>
					{' '}
					Select RG &amp; Resource{' '}
				</ProgressTrackerItem>
				<ProgressTrackerItem completed={currentStep > 3} current={currentStep === 3}>
					{' '}
					Select Assignment{' '}
				</ProgressTrackerItem>
				<ProgressTrackerItem completed={currentStep > 4} current={currentStep === 4}>
					{' '}
					Select Policy{' '}
				</ProgressTrackerItem>
				<ProgressTrackerItem completed={currentStep > 5} current={currentStep === 5}>
					{' '}
					Review &amp; Submit{' '}
				</ProgressTrackerItem>
			</ProgressTracker>
			<br />
			{renderStep()}
		</SectionHomeWrapper>
	);
};

export default PolicyExemptionHome;
